import { BOOKS, toSlug, type CommentarySummary } from "@bibleunleashed/shared";
import { useState } from "react";
import { useLocation } from "wouter";
import { useCommentaryIndex } from "@/lib/commentary";

/**
 * Book narrows the chapter list; only picking a chapter (or Previous/Next) navigates.
 * Scoped to what's published, which is already in canonical order — the same shape as
 * the "Pick a chapter" widget on the WordPress site it replaces.
 */
function Picker({ chapters }: { chapters: CommentarySummary[] }) {
  const [, navigate] = useLocation();
  // Undefined until the reader picks a book — the index list arrives async,
  // so a value baked into useState's initializer at mount could stick to "no chapters yet".
  const [bookId, setBookId] = useState<string>();
  const first = chapters[0];

  if (!first) {
    return <p className="font-serif text-ink-3">Nothing published yet — check back soon.</p>;
  }

  const book = bookId ?? first.book;
  const books = new Map(chapters.map((c) => [c.book, BOOKS.find((b) => b.id === c.book)?.name ?? c.book]));
  const inBook = chapters.filter((c) => c.book === book);
  const index = chapters.findIndex((c) => c.book === book);

  const open = (i: number) => {
    const target = chapters[i];
    if (!target) return;
    setBookId(target.book);
    navigate(`/commentary/${toSlug({ book: target.book, chapter: target.chapter })}`);
  };

  return (
    <div className="rounded-lg border border-rule bg-paper-2 p-5">
      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="flex-1 font-sans text-[12px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
          Book
          <select
            value={book}
            onChange={(e) => setBookId(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-rule bg-paper px-3 py-2.5 font-serif text-[15px] normal-case text-ink"
          >
            {Array.from(books, ([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </label>
        <label className="flex-1 font-sans text-[12px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
          Chapter
          <select
            value=""
            onChange={(e) => open(chapters.findIndex((c) => c.book === book && c.chapter === Number(e.target.value)))}
            className="mt-1.5 w-full rounded-md border border-rule bg-paper px-3 py-2.5 font-serif text-[15px] normal-case text-ink"
          >
            <option value="" disabled>Select chapter</option>
            {inBook.map((c) => (
              <option key={c.chapter} value={c.chapter}>{c.chapter}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-3 flex gap-3">
        <button
          disabled={index <= 0}
          onClick={() => open(index - 1)}
          className="flex-1 rounded-md bg-accent px-4 py-2.5 text-[14px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          ← Previous chapter
        </button>
        <button
          disabled={index < 0 || index === chapters.length - 1}
          onClick={() => open(index + 1)}
          className="flex-1 rounded-md bg-accent px-4 py-2.5 text-[14px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next chapter →
        </button>
      </div>
    </div>
  );
}

export function ChapterPicker() {
  const { data: chapters } = useCommentaryIndex();
  const published = chapters?.filter((c) => c.status === "published") ?? [];

  return (
    <div>
      <p className="font-sans text-[12px] tracking-[0.12em] text-ink-3 uppercase">Pick a chapter</p>
      <div className="mt-3">
        <Picker chapters={published} />
      </div>
    </div>
  );
}
